import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Layout, Zap, MessageSquare, Settings, LogOut, Plus, ChevronsUpDown, Folder, Check, ChevronLeft, ChevronRight, Menu, X, Fingerprint, Sparkles, ScrollText, ShieldCheck, Columns3, Sun, Moon, Megaphone, BarChart3 } from 'lucide-react';
import { Logo } from '@/components/core/Logo';
import { useAuthStore } from '@/contexts/authStore';
import { useUIStore } from '@/contexts/uiStore';
import { useThemeStore } from '@/contexts/themeStore';
import { useState, useEffect, useRef } from 'react';
import { useProjects, type Project } from '@/api/hooks/useProjects';
import { useIsFreeUser } from '@/api/hooks/useSubscription';
import { isFeatureBlockedForSlug } from '@/config/plans';
import CreateProjectModal from './CreateProjectModal';
import PricingModal from './PricingModal';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface SidebarProps {
  onMainNavigationAttempt?: () => boolean;
}

export default function Sidebar({ onMainNavigationAttempt }: SidebarProps = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();

  const { user, logout } = useAuthStore();
  const { sidebarCollapsed: isCollapsed, toggleSidebar } = useUIStore();
  const { resolved, toggle: toggleTheme } = useThemeStore();

  const { data: projects = [] } = useProjects();
  const { isFree, subscription } = useIsFreeUser();

  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Set current project from URL and persist to localStorage
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const found = projects.find((p) => p.id === projectId);
      if (found) {
        setCurrentProject(found);
        localStorage.setItem('thecontent_last_project_id', projectId);
      }
    }
  }, [projectId, projects]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProjectsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProjectSelect = (project: Project) => {
    setCurrentProject(project);
    setIsProjectsOpen(false);
    navigate(`/dashboard/project/${project.id}`);
  };

  const isOwnProject = !!currentProject && currentProject.owner_id === user?.uid;
  const canManageProjectConfig = Boolean(user?.is_global_admin || isOwnProject);

  // Starter (funil R$29): transcrição + biblioteca — sem Studio/Kanban/DNA/Prompts
  const planSlug = subscription?.plan_slug;
  const featureBlocked = (feature: string) =>
    !!currentProject && isOwnProject && isFeatureBlockedForSlug(planSlug, feature);

  const navItems = [
    { name: t('nav.overview'), key: 'Overview', href: currentProject ? `/dashboard/project/${currentProject.id}/overview` : '/dashboard', icon: Layout },
    { name: t('nav.inspiration'), key: 'Inspiration', href: currentProject ? `/dashboard/project/${currentProject.id}/inspirations` : '/dashboard', icon: Zap },
    !featureBlocked('documents') && { name: t('nav.studio', 'Documentos'), key: 'Studio', href: currentProject ? `/dashboard/project/${currentProject.id}/studio` : '/dashboard', icon: MessageSquare },
    !featureBlocked('kanban') && { name: t('nav.kanban', 'Kanban'), key: 'Kanban', href: currentProject ? `/dashboard/project/${currentProject.id}/kanban` : '/dashboard', icon: Columns3 },
    !featureBlocked('creative_dna') && { name: t('nav.creativeDna'), key: 'Creative DNA', href: currentProject ? `/dashboard/project/${currentProject.id}/creative-dna` : '/dashboard', icon: Fingerprint },
    !featureBlocked('prompts') && { name: t('nav.prompts', 'Meus Prompts'), key: 'Prompts', href: currentProject ? `/dashboard/project/${currentProject.id}/prompts` : '/dashboard', icon: ScrollText },
    ...(canManageProjectConfig
      ? [{ name: t('nav.settings'), key: 'Settings', href: currentProject ? `/dashboard/project/${currentProject.id}/settings` : '/dashboard', icon: Settings }]
      : []),
  ].filter(Boolean) as { name: string; key: string; href: string; icon: typeof Layout }[];

  const adminItems = user?.is_global_admin
    ? [
        { name: 'Clientes', key: 'AdminCustomers', href: '/admin/clientes', icon: ShieldCheck },
        { name: 'Templates Prompts', key: 'AdminTemplates', href: '/admin/templates', icon: ScrollText },
        { name: 'Headlines', key: 'AdminHeadlines', href: '/admin/headlines', icon: Megaphone },
        { name: 'Custos IA', key: 'AdminCosts', href: '/admin/costs', icon: BarChart3 },
      ]
    : [];
  const shouldShowUpgrade = currentProject && isOwnProject && isFree && !user?.is_global_admin;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="fixed top-4 left-4 z-50 p-2 bg-surface rounded-lg shadow-lg lg:hidden border border-border"
      >
        <Menu className="h-5 w-5 text-foreground" />
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        ${isCollapsed ? 'w-20' : 'w-64'}
        h-screen bg-surface border-r border-border flex flex-col fixed left-0 top-0 z-40 shadow-sm transition-all duration-300
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo & Close Button */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <Link to="/dashboard" className={isCollapsed ? 'mx-auto' : ''}>
            <Logo variant={isCollapsed ? 'icon' : 'full'} size="sm" />
          </Link>
          <button
            onClick={() => setIsMobileOpen(false)}
            className="lg:hidden p-1 hover:bg-secondary rounded-lg"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Project Switcher */}
        <div className="px-4 py-4" ref={dropdownRef}>
          <button
            onClick={() => setIsProjectsOpen(!isProjectsOpen)}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-2 rounded-xl border border-border hover:bg-secondary transition-colors group`}
          >
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} overflow-hidden`}>
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                <Folder className="w-4 h-4" />
              </div>
              {!isCollapsed && (
                <div className="text-left overflow-hidden">
                  <p className="text-sm font-medium text-foreground truncate">
                    {currentProject ? currentProject.name : t('project.selectProject')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {currentProject ? t('project.activeWorkspace') : t('project.noProjectSelected')}
                  </p>
                </div>
              )}
            </div>
            {!isCollapsed && <ChevronsUpDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {/* Dropdown */}
          {isProjectsOpen && (
            <div className={`absolute ${isCollapsed ? 'left-20 w-64' : 'left-4 right-4'} mt-2 bg-surface border border-border rounded-xl shadow-lg z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200`}>
              <div className="p-2 max-h-60 overflow-y-auto">
                <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('project.projects')}</p>
                {projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleProjectSelect(project)}
                    className={`w-full flex items-center justify-between px-2 py-2 rounded-lg text-sm transition-colors ${
                      currentProject?.id === project.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground hover:bg-secondary'
                    }`}
                  >
                    <span className="truncate">{project.name}</span>
                    {currentProject?.id === project.id && <Check className="w-3 h-3" />}
                  </button>
                ))}
                {projects.length === 0 && (
                  <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
              <div className="p-2 border-t border-border bg-muted">
                <button
                  onClick={() => {
                    setIsProjectsOpen(false);
                    if (projects.length > 0) {
                      toast.error(t('project.toast.multipleProjectsComingSoon'));
                      return;
                    }
                    setIsCreateModalOpen(true);
                  }}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <Plus className="w-4 h-4" /> {t('project.createNewProject')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {!isCollapsed && <p className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('nav.menu')}</p>}
          {navItems.map((item) => {
            // Only Overview should be active on /dashboard when no project is selected
            const isOverviewOnDashboard = item.key === 'Overview' && pathname === '/dashboard';
            const isExactMatch = pathname === item.href && item.href !== '/dashboard';
            const isPathMatch = item.href !== '/dashboard' && pathname.startsWith(item.href + '/');
            const isActive = isOverviewOnDashboard || isExactMatch || isPathMatch || (pathname === item.href && currentProject !== null);
            const isDisabled = !currentProject && item.key !== 'Overview';

            return (
              <Link
                key={item.key}
                to={isDisabled ? '#' : item.href}
                onClick={(e) => {
                  if (isDisabled && item.key !== 'Overview') e.preventDefault();
                  if (!isDisabled && onMainNavigationAttempt?.()) {
                    e.preventDefault();
                  }
                  setIsMobileOpen(false);
                }}
                title={isCollapsed ? item.name : undefined}
                className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : isDisabled
                      ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                      : 'text-foreground hover:bg-secondary'
                }`}
              >
                <item.icon className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                {!isCollapsed && item.name}
              </Link>
            );
          })}
          {adminItems.length > 0 && (
            <>
              {!isCollapsed && <p className="px-4 pt-4 pb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Admin</p>}
              {adminItems.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    onClick={() => setIsMobileOpen(false)}
                    title={isCollapsed ? item.name : undefined}
                    className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                    {!isCollapsed && item.name}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Upgrade Button (only for free tier users) */}
        {shouldShowUpgrade && (
          <div className={`px-4 ${isCollapsed ? 'py-2' : 'py-3'} border-t border-border`}>
            <button
              onClick={() => setIsPricingModalOpen(true)}
              title={isCollapsed ? t('upgrade.upgrade') : undefined}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-4 py-2.5 rounded-xl text-sm font-medium transition-all bg-gradient-to-r from-primary to-secondary-pink text-white hover:opacity-90 shadow-md`}
            >
              <Sparkles className="w-4 h-4" />
              {!isCollapsed && t('upgrade.upgrade')}
            </button>
          </div>
        )}

        {/* User Profile & Logout */}
        <div className="p-4 border-t border-border">
          {!isCollapsed ? (
            <>
              <div className="flex items-center gap-3 mb-3 px-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-xs font-bold text-white">
                  {user?.name?.[0] || 'U'}
                </div>
                <div className="flex-1 overflow-hidden">
                  <p className="text-sm font-medium text-foreground truncate">{user?.name || t('user.user')}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground hover:text-error transition-colors py-2 hover:bg-error/10 rounded-lg mb-3"
              >
                <LogOut className="w-3 h-3" /> {t('auth.signOut')}
              </button>
            </>
          ) : (
            <button
              onClick={handleLogout}
              title={t('auth.signOut')}
              className="w-full flex items-center justify-center p-2 text-muted-foreground hover:text-error transition-colors hover:bg-error/10 rounded-lg mb-3"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}

          {/* Theme Toggle */}
          {isCollapsed ? (
            <button
              onClick={toggleTheme}
              title={resolved === 'dark' ? 'Modo claro' : 'Modo escuro'}
              className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-muted transition-colors group mb-2"
            >
              <div className={`relative w-8 h-4 rounded-full transition-colors duration-500 ${resolved === 'dark' ? 'bg-amber-400/20' : 'bg-sky-400/20'}`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full shadow-sm transition-all duration-500 flex items-center justify-center ${resolved === 'dark' ? 'left-0.5 bg-amber-400' : 'left-[18px] bg-sky-400'}`}>
                  {resolved === 'dark' ? (
                    <Moon className="w-2 h-2 text-white" />
                  ) : (
                    <Sun className="w-2 h-2 text-white" />
                  )}
                </div>
              </div>
            </button>
          ) : (
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted transition-colors group mb-2"
            >
              <div className={`relative w-10 h-5 rounded-full transition-colors duration-500 ${resolved === 'dark' ? 'bg-amber-400/20' : 'bg-sky-400/20'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full shadow-sm transition-all duration-500 flex items-center justify-center ${resolved === 'dark' ? 'left-0.5 bg-amber-400' : 'left-[22px] bg-sky-400'}`}>
                  {resolved === 'dark' ? (
                    <Moon className="w-2.5 h-2.5 text-white" />
                  ) : (
                    <Sun className="w-2.5 h-2.5 text-white" />
                  )}
                </div>
              </div>
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {resolved === 'dark' ? 'Escuro' : 'Claro'}
              </span>
            </button>
          )}

          {/* Toggle Collapse Button */}
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex w-full items-center justify-center gap-2 px-3 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors"
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-xs font-medium">{t('nav.collapse')}</span>
              </>
            )}
          </button>
        </div>

        <CreateProjectModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />

        {currentProject && (
          <PricingModal
            isOpen={isPricingModalOpen}
            onClose={() => setIsPricingModalOpen(false)}
            projectId={currentProject.id}
          />
        )}
      </aside>
    </>
  );
}
